import { LiveAnnouncer } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty, coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import { CdkScrollable } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatSelectionListChange } from '@angular/material/list';
import { PageEvent } from '@angular/material/paginator';
import { SortDirection } from '@angular/material/sort';
import { Code, trackByCoreResource } from '@mtna/pojo-consumer-ts';

import { CanLoad, CanLoadCtor, mixinLoading } from '../core/common-behaviors';

/* eslint-disable @angular-eslint/no-inputs-metadata-property */

// Boilerplate for applying mixins to RdsCodeSelectComponent.
class RdsCodeSelectBase {}
const _RdsCodeSelectBase: CanLoadCtor = mixinLoading(RdsCodeSelectBase);

export type RdsCodeSelectSelectionType = 'single' | 'multi';

@Component({
  selector: 'rds-c-code-select',
  templateUrl: './code-select.component.html',
  styleUrls: ['./code-select.component.scss'],
  inputs: ['loading'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsCodeSelectComponent extends _RdsCodeSelectBase implements CanLoad {
  static ngAcceptInputType_codes: Code[] | null | undefined;
  static ngAcceptInputType_enablePagination: BooleanInput;
  static ngAcceptInputType_enableSearch: BooleanInput;
  static ngAcceptInputType_enableSort: BooleanInput;
  static ngAcceptInputType_length: NumberInput;
  static ngAcceptInputType_loading: BooleanInput;
  static ngAcceptInputType_pageIndex: NumberInput;
  static ngAcceptInputType_pageSize: NumberInput;
  static ngAcceptInputType_selectedCodes: Code[] | null | undefined;
  static ngAcceptInputType_sortDirection: SortDirection | null | undefined;

  /** Aria-label to include on the underlying list element. Defaults to 'Select codes' */
  @Input() ariaLabel: string | null | undefined = $localize`Select codes`;

  /** Required codes to select from */
  @Input()
  get codes(): Array<Code> {
    return this._codes;
  }
  set codes(codesArray: Array<Code>) {
    this._codes = codesArray || [];
    this._updateDisplayedCodes();
  }
  private _codes: Array<Code> = [];

  /** Optional ngStyles for code list, defaults to `{ height: 'auto' }`*/
  @Input() codeListStyles: { [key: string]: string } = { height: 'auto' };

  @Input()
  get sortDirection(): SortDirection {
    return this._sortDirection;
  }
  set sortDirection(direction: SortDirection) {
    this._sortDirection = direction || '';
    this._sortButtonDetails[this._sortDirection];
  }
  private _sortDirection: SortDirection = '';

  /** The length of the total number of items that are being paginated. Defaulted to 0. */
  @Input()
  get length(): number {
    return this._length;
  }
  set length(value: number) {
    this._length = coerceNumberProperty(value);
  }
  private _length = 0;

  /** Whether to enable pagination */
  @Input()
  get enablePagination() {
    return this._enablePagination;
  }
  set enablePagination(value: boolean) {
    this._enablePagination = coerceBooleanProperty(value);
  }
  private _enablePagination = false;

  /** Whether to enable text search */
  @Input()
  get enableSearch() {
    return this._enableSearch;
  }
  set enableSearch(value: boolean) {
    this._enableSearch = coerceBooleanProperty(value);
  }
  private _enableSearch = false;

  /** Whether to enable to sort button */
  @Input()
  get enableSort() {
    return this._enableSort;
  }
  set enableSort(value: boolean) {
    this._enableSort = coerceBooleanProperty(value);
  }
  private _enableSort = false;

  /** The zero-based page index of the displayed list of codes. Defaulted to 0. */
  @Input()
  get pageIndex(): number {
    return this._pageIndex;
  }
  set pageIndex(value: number) {
    this._pageIndex = Math.max(coerceNumberProperty(value), 0);
  }
  private _pageIndex = 0;

  /** Number of items to display on a page. */
  @Input()
  get pageSize(): number | undefined {
    return this._pageSize;
  }
  set pageSize(value: number | undefined) {
    this._pageSize = Math.max(coerceNumberProperty(value), 0);
  }
  private _pageSize?: number;

  /** Code(s) the user selected */
  @Input()
  get selectedCodes(): Array<Code> {
    return this._selectedCodes;
  }
  set selectedCodes(codes: Array<Code>) {
    this._selectedCodes = codes || [];
    this._selectedUris = {};
    this._selectedCodes.forEach((c) => {
      if (c.uri) {
        this._selectedUris[c.uri] = true;
      }
    });
    this._updateDisplayedCodes();
  }
  private _selectedCodes: Array<Code> = [];

  /** Search text */
  @Input() searchText: string | null | undefined;
  /** Selection type, can be single or multi, defaults to multi */
  @Input() selectionType: RdsCodeSelectSelectionType = 'multi';

  /** Event emitted when the user clears selected codes */
  @Output() clearSelectedCodes = new EventEmitter<null>();
  /** Event emitted when the paginator changes the page size or page index */
  @Output() pageChange = new EventEmitter<PageEvent>();
  /** Text search in codes */
  @Output() search = new EventEmitter<string>();
  /** Event emitted when the user selected a code */
  @Output() selectCode = new EventEmitter<Code>();

  @Output() sortCodes = new EventEmitter<SortDirection>();
  /** Event emitted when the user unselects a code */
  @Output() unselectCode = new EventEmitter<Code>();

  /** the scrollable list when many codes exist. Used for scrollTop on pagination and search */
  @ViewChild(CdkScrollable) scrollContainer!: CdkScrollable;

  /** TrackBy function for the *ngFor loop of codes */
  trackByCr = trackByCoreResource;

  /**
   * Object map of selected Code uris.
   *
   * This was necessary because the mat-selection-list was not showing
   * the correct selected option if objects were not the same dispite the compareWith function.
   * As of material v7.3.1
   */
  _selectedUris: { [uri: string]: boolean } = {};

  /** Array of codes being displayed in component. */
  _displayedCodes: Code[] = [];

  _sortButtonDetails: { [direction: string]: string } = {
    '': $localize`Sort Codes`,
    asc: $localize`Sorting codes ascending`,
    desc: $localize`Sorting codes descending`,
  };

  constructor(private _liveAnnouncer: LiveAnnouncer) {
    super();
  }

  /**
   * Function used for comparing codes in a mat-selection-list.
   */
  _compareCodes(c1: Code, c2: Code): boolean {
    return c1 && c2 ? c1.uri === c2.uri : c1 === c2;
  }

  /**
   * Sets the displayed codes so that any selected codes not in the current pagination are displayed above all other codes.
   */
  _updateDisplayedCodes() {
    // When selected codes aren't included in the list of searched codes, we want to display them as well
    const userSelectedCodes = this.selectedCodes.filter((s) => !this.codes.some((c) => c.uri === s.uri));
    this._displayedCodes = [...userSelectedCodes, ...this.codes];
  }

  /**
   * When a code is selected/unselected from a mat-selection-list,
   * emit the appropriate event.
   *
   * @param change change event that describes which code was selected/unselected
   */
  _handleSelectionListChange(change: MatSelectionListChange) {
    const code = change.option.value;
    if (!change.option.selected) {
      // If the unselected displayed code is not part of the original list of codes, then it was only selected and should be removed
      if (!this.codes.some((c) => c.uri === code.uri)) {
        this._displayedCodes = this._displayedCodes.filter((c) => c.uri !== code.uri);
      }
      this.unselectCode.emit(code);
    } else {
      this.selectCode.emit(code);
    }
  }

  _handleCodeSort(currentSortValue: SortDirection) {
    let newSortDirection: SortDirection;
    switch (currentSortValue) {
      case 'asc':
        this._liveAnnouncer.announce($localize`:Notification|informs the user about list sorting:Sorting codes descending`);
        newSortDirection = 'desc';
        break;
      case 'desc':
        this._liveAnnouncer.announce($localize`:Notification|informs the user sorting is removed:Sorting cleared`);
        newSortDirection = '';
        break;
      default:
        this._liveAnnouncer.announce($localize`:Notification|informs the user about list sorting:Sorting codes ascending`);
        newSortDirection = 'asc';
        break;
    }
    this.sortCodes.emit(newSortDirection);
  }

  /**
   * When a code is selected in a mat-radio group,
   * emit events to unselect the previously selected code
   * and emit an event to select the new code.
   *
   * @param code the code that is selected
   */
  _handleRadioGroupChange(code: Code) {
    if (this.selectedCodes && this.selectedCodes.length) {
      this.unselectCode.emit(this.selectedCodes[0]);
    }
    this.selectCode.emit(code);
  }

  _scrollToTop() {
    if (this.scrollContainer) {
      this.scrollContainer.scrollTo({ top: 0 });
    }
  }
}
